/**
 * @file        src/pages/insurance/BookingDetailsDrawer.jsx
 * @description One travel-insurance policy in full: plan and cover, the insured
 *              travellers with their own policy numbers, the money split
 *              (premium / our markup / wallet vs gateway) and the refund state.
 *              Opened from either Insurance List row menu.
 *
 *              `details` is the supplier's stored payload, parsed server-side —
 *              the same object the customer's own copy is built from, so the two
 *              cannot describe one policy differently. It is legitimately EMPTY
 *              for a Failed or Initiated booking, where the insurer answered
 *              nothing; every block that reads it degrades to the row's own
 *              columns rather than rendering blanks.
 * @author      Kinetika Labs
 * @created     2026-09-22
 * @changelog
 *   - 2026-10-09 Claude — Full-screen button beside the close X opens these details as
 *                          their own page in a new tab (`page` prop renders without
 *                          the modal overlay — route /insurance/bookings/:id/details).
 *   - 2026-09-25 Claude — Share by Email (Tax Invoice / the insurer's Policy
 *     Document / both), as the flight and hotel drawers have had since
 *     2026-08-04. `policy_doc_status` rides on the detail response so the option
 *     is offered identically whether the share is opened from here or the list.

 *   - 2026-09-23 Claude — Takes the screen's `audience` so its document links
 *                          record where they were opened from (lib/docOrigin) —
 *                          a corporate booking's invoice then keeps the
 *                          Corporate sidebar group open instead of the B2C one.
 *   - 2026-09-22 Claude — Initial (B2C Insurance + Corporate Insurance).
 *   - 2026-09-25 Claude — The drawer is a FIXED box that scrolls inside
 *                          (`.lk-modal.lk-tall` + one `.lk-modal-body` wrapping all
 *                          three states) and its loading state is a DrawerSkeleton
 *                          shaped like the body, not a one-line "Loading…". It used
 *                          to open at that line's height and jump when the data
 *                          landed — measured 244px → 1,410px on this drawer — then
 *                          again as the amendments and timeline queries answered
 *                          separately. Now the frame never resizes once the body is
 *                          at the viewport ceiling, so a late query moves nothing.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import FullScreenButton from '../../components/FullScreenButton';
import DrawerSkeleton from '../../components/DrawerSkeleton';
import ShareEmailModal from '../../components/ShareEmailModal';
import { get } from '../../api/client';
import { docPath } from '../../lib/docOrigin';
import { money, day, dateTime, statusTone, journeyLabel, docAccess } from './cells';

function Row({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children ?? <span className="muted">—</span>}</div></div>
  );
}

export default function BookingDetailsDrawer({ bookingId, audience = 'b2c', onClose, page = false }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['insurance-booking-details', bookingId],
    queryFn: () => get(`/insurance/bookings/${bookingId}`),
    enabled: !!bookingId,
  });
  const [shareOpen, setShareOpen] = useState(false);
  const b = data?.data;
  const docs = docAccess(b?.booking_status);
  const d = b?.details || {};

  // The insurer's own per-traveller rows. Absent on a policy that never issued.
  const travellers = Array.isArray(d.travellers) ? d.travellers : [];
  // The insurer's fare split: SP is the net premium, SPGST the tax on it. Used
  // only when present — a guessed split on a money screen is worse than none.
  const fare = d.fare || {};
  const hasSplit = Number(fare.SP) > 0;

  const walletPaid = Number(b?.wallet_paid) || 0;
  const gatewayPaid = Number(b?.gateway_paid) || 0;

  return (
    <>
    <div className={page ? 'booking-page' : 'lk-overlay'} onMouseDown={(e) => !page && e.target === e.currentTarget && onClose()}>
      <div className="lk-modal lk-tall panel glass" style={page ? { padding: 24 } : { maxWidth: 760, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>Insurance Policy {b ? `· #${b.id}` : ''}</h3>
          <span style={{ display: 'inline-flex', gap: 8 }}>
            {!page ? <FullScreenButton product="insurance" bookingId={bookingId} audience={audience} /> : null}
            <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
          </span>
        </div>

        <div className="lk-modal-body">
        {isLoading ? <DrawerSkeleton cells={6} blocks={2} />
          : isError || !b ? <div className="empty">Could not load this policy.</div>
            : (
              <div className="det-body">
                <div className="det-grid">
                  <Row label="Booking Ref"><b className="mono">{b.booking_reference || '—'}</b></Row>
                  <Row label="Status"><Chip tone={statusTone(b.booking_status)}>{b.booking_status || '—'}</Chip></Row>
                  <Row label="Plan">{journeyLabel(b.journey)}</Row>
                  <Row label="Tier / Cover">{[b.plan_tier, b.plan_cover].filter(Boolean).join(' · ') || null}</Row>
                  <Row label="Insurer">{b.insurer}</Row>
                  <Row label="Policy No.">
                    {b.policy_id
                      ? <span className="mono">{b.policy_id}</span>
                      : <span className="muted">{docs.confirmed ? 'Not issued by the insurer' : '—'}</span>}
                  </Row>
                  <Row label="Destination">{b.destination}</Row>
                  <Row label="Cover">{`${day(b.start_date)} – ${day(b.end_date)}`}</Row>
                  <Row label="Travellers">{b.travellers == null ? null : String(b.travellers)}</Row>
                  <Row label="Brand"><span className="mono">{b.brand || '—'}</span></Row>
                  <Row label="Booked">{dateTime(b.created_at)}</Row>
                  <Row label="Invoice No.">
                    {b.invoice_number
                      ? <span className="mono">{b.invoice_number}</span>
                      : <span className="muted">Not issued</span>}
                  </Row>
                </div>

                {/* Buyer. A corporate policy names the company as well, so it is
                    obvious which account the spend belongs to. */}
                <p className="doc-h">Customer</p>
                <div className="det-grid">
                  <Row label="Name">{b.customer_name}</Row>
                  <Row label="Email">{b.customer_email}</Row>
                  <Row label="Phone">{b.customer_phone}</Row>
                  <Row label="Account">
                    {b.company_name
                      ? <>{b.company_name} <Chip tone="ok">Corporate</Chip></>
                      : (!b.user_id || Number(b.user_id) === 1 ? <Chip tone="warn">Guest</Chip> : 'Retail')}
                  </Row>
                </div>

                {travellers.length ? (
                  <>
                    <p className="doc-h">Insured travellers</p>
                    <div className="doc-table-wrap">
                      <table className="doc-table doc-gst doc-table-wide" style={{ minWidth: 520 }}>
                        <thead>
                          <tr><th>S No.</th><th>Name</th><th>Policy No.</th><th className="doc-num">Premium</th></tr>
                        </thead>
                        <tbody>
                          {travellers.map((t, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{t.name || '—'}</td>
                              <td className="mono">{t.policyId || 'Not issued'}</td>
                              <td className="doc-num">{money(t.premium)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : null}

                <p className="doc-h">Payment</p>
                <div className="det-grid">
                  {hasSplit ? <Row label="Net premium">{money(fare.SP)}</Row> : null}
                  {hasSplit ? <Row label="Premium GST">{money(fare.SPGST)}</Row> : null}
                  <Row label="Premium charged">{money(b.premium)}</Row>
                  <Row label="Our markup">{money(b.markup)}</Row>
                  <Row label="Total paid"><b>{money(b.total_paid)}</b></Row>
                  <Row label="Wallet / Gateway">
                    {`${money(walletPaid)} / ${money(gatewayPaid)}`}
                  </Row>
                  {b.refund_status ? (
                    <Row label="Refund">
                      <Chip tone={statusTone(b.refund_status)}>{b.refund_status}</Chip>
                      {b.refund_amount != null ? ` ${money(b.refund_amount)}` : ''}
                    </Row>
                  ) : null}
                  {b.cancelled_at ? <Row label="Cancelled">{dateTime(b.cancelled_at)}</Row> : null}
                </div>

                {/* Why a Failed booking failed — the single most useful field on
                    this screen when a customer calls about a charge. */}
                {b.book_failed_reason ? (
                  <>
                    <p className="doc-h">Supplier response</p>
                    <p className="muted">{b.book_failed_reason}</p>
                  </>
                ) : null}

                {/* The invoice renders INSIDE the console (an admin-native doc
                    page), never a redirect to the customer site — same as the
                    flight and hotel drawers. Which documents exist is the one
                    docAccess() rule. */}
                <div className="form-actions" style={{ flexWrap: 'wrap', gap: 8 }}>
                  {docs.invoice ? (
                    <Link className="btn ghost" to={docPath(`/insurance/bookings/${b.id}/invoice`, audience)} onClick={onClose}>
                      <Icon name="download" /> Tax Invoice
                    </Link>
                  ) : null}
                  {/* Tax Invoice and/or the INSURER's policy document. Same
                      docAccess() rule as the flight and hotel drawers. */}
                  {docs.share ? (
                    <button className="btn ghost" onClick={() => setShareOpen(true)}>
                      <Icon name="mail" /> Share by Email
                    </button>
                  ) : null}
                  <button className="btn ghost" onClick={onClose}>Close</button>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>

    {shareOpen && b ? (
      <ShareEmailModal kind="insurance" booking={b} onClose={() => setShareOpen(false)} />
    ) : null}
    </>
  );
}
